/*
 * Client-Catalog admin surface — /settings/client-catalog. Master-detail
 * view for the ClientProduct catalog: left side lists every product with
 * search + status-filter chips, right side is a stacked-section form for
 * the selected product's full metadata. Approval transitions and tenant
 * exclusions live at the bottom of the form so the two orthogonal axes
 * (Freigabe = catalog-admin, Sperren = tenant-admin) stay visible without
 * shifting the operator between tabs.
 *
 * Permissions:
 *   - `catalog.admin` gates create / edit / delete / approval transitions.
 *   - `catalog.tenant.admin` gates the Sperren-section (exclusion add/remove).
 *   - Any authenticated user can read the exclusions list (needed for the
 *     Sperren-section's current-state read even without tenant.admin).
 *
 * The list uses `includeAll=true` when the caller has `catalog.admin` so
 * admins see draft/pending/rejected/retired products too; non-admins only
 * see Approved+enabled+non-excluded (backend enforces the same rule).
 */

import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import { sessionSignal } from '../auth/session';
import {
  ClientLinuxGateway,
  ClientAndroidPanel,
  ClientWindowsPc,
  ClientIosPanel,
  ClientEmbedded,
  IconSave,
  IconCancel,
  IconCreate,
  IconDeleteHard,
} from '../icons/HvacIcons';
import type {
  ClientProductApprovalStatus,
  ClientProductCreateRequest,
  ClientProductDeviceKind,
  ClientProductDetailDto,
  ClientProductOsKind,
  ClientProductSummaryDto,
  ClientProductUpdateRequest,
  CraRiskClass,
  ListClientProductsResponse,
  ListTenantExclusionsResponse,
  SbomFormatWire,
  SetApprovalRequest,
  TenantProductExclusionDto,
  TenantProductExclusionRequest,
} from '../api/types';
import './ClientCatalogPage.css';

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------

const OS_KIND_OPTIONS: ClientProductOsKind[] = [
  'linux',
  'android',
  'windows',
  'ios',
  'embedded',
  'unknown',
];
const DEVICE_KIND_OPTIONS: ClientProductDeviceKind[] = [
  'gateway',
  'panel',
  'pc',
  'embedded',
  'unknown',
];
const CRA_OPTIONS: CraRiskClass[] = ['standard', 'important', 'critical'];
const SBOM_OPTIONS: SbomFormatWire[] = ['none', 'cyclonedx', 'spdx'];

const STATUS_LABEL: Record<ClientProductApprovalStatus, string> = {
  draft: 'Draft',
  pending_review: 'In Prüfung',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
  retired: 'Retired',
};

/** Icon for a product row / list item. Picks by (osKind, deviceKind) — the
 *  panel/gateway split dominates the visual for tablet-vs-cabinet devices. */
export function ProductIcon({
  osKind,
  deviceKind,
  size = 20,
}: {
  osKind: ClientProductOsKind | null | undefined;
  deviceKind: ClientProductDeviceKind | null | undefined;
  size?: number;
}) {
  const style = { color: 'var(--text-secondary)' };
  // Panel + iOS wins over the OS-Kind decision because "iOS-Panel" is the
  // only overlap where the OS narrative reads stronger than the form-factor
  // (iOS never runs on cabinet hardware). Android → panel by convention.
  if (osKind === 'ios') return <ClientIosPanel size={size} style={style} />;
  if (osKind === 'android') return <ClientAndroidPanel size={size} style={style} />;
  if (osKind === 'windows') return <ClientWindowsPc size={size} style={style} />;
  if (deviceKind === 'embedded' || osKind === 'embedded')
    return <ClientEmbedded size={size} style={style} />;
  // Default: linux (or unknown) + gateway/pc/unknown → linux-gateway glyph.
  return <ClientLinuxGateway size={size} style={style} />;
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

type StatusFilter = 'all' | ClientProductApprovalStatus;

export function ClientCatalogPage() {
  const perms = sessionSignal.value.user?.permissions ?? [];
  const canAdmin = perms.includes('catalog.admin');
  const canTenantAdmin = perms.includes('catalog.tenant.admin');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const listQuery = useMemo(() => {
    const params = new URLSearchParams();
    // Admins want the full catalog by default; non-admins get the effective
    // (approved+enabled+non-excluded) view.
    if (canAdmin) params.set('includeAll', 'true');
    if (statusFilter !== 'all') params.set('status', statusFilter);
    return params.toString();
  }, [canAdmin, statusFilter]);

  const listState = useAsync(
    (signal) =>
      api.get<ListClientProductsResponse>(
        `/api/client-catalog/products?${listQuery}`,
        signal,
      ),
    [listQuery, refreshTick],
  );

  const items: ClientProductSummaryDto[] =
    listState.status === 'ok' ? listState.data.items : [];

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (p) =>
        p.displayName.toLowerCase().includes(needle) ||
        p.key.toLowerCase().includes(needle) ||
        (p.manufacturerName?.toLowerCase().includes(needle) ?? false),
    );
  }, [items, search]);

  const refreshAll = () => setRefreshTick((n) => n + 1);
  const openCreate = () => setSelectedId('__new__');

  const actions = canAdmin ? (
    <button type="button" class="hv-btn hv-btn--primary hv-btn--sm" onClick={openCreate}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <IconCreate size={14} />
        Neues Produkt
      </span>
    </button>
  ) : undefined;

  return (
    <AppShell title="Client-Katalog" actions={actions}>
      <div class="hv-catalog">
        <aside class="hv-catalog__master">
          <div class="hv-catalog__master-toolbar">
            <div class="hv-catalog__master-search">
              <input
                type="search"
                placeholder="Suche in Name, Key, Hersteller…"
                value={search}
                onInput={(e) => setSearch((e.currentTarget as HTMLInputElement).value)}
              />
            </div>
            <div class="hv-catalog__chips" role="tablist" aria-label="Status-Filter">
              <StatusChip label="Alle" active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
              <StatusChip label="Draft" active={statusFilter === 'draft'} onClick={() => setStatusFilter('draft')} />
              <StatusChip label="In Prüfung" active={statusFilter === 'pending_review'} onClick={() => setStatusFilter('pending_review')} />
              <StatusChip label="Freigegeben" active={statusFilter === 'approved'} onClick={() => setStatusFilter('approved')} />
              <StatusChip label="Abgelehnt" active={statusFilter === 'rejected'} onClick={() => setStatusFilter('rejected')} />
              <StatusChip label="Retired" active={statusFilter === 'retired'} onClick={() => setStatusFilter('retired')} />
            </div>
          </div>

          <div class="hv-catalog__master-list">
            {listState.status === 'loading' && (
              <div class="hv-catalog__empty">Lade Produkte…</div>
            )}
            {listState.status === 'error' && (
              <div class="hv-catalog__empty">Konnte Katalog nicht laden.</div>
            )}
            {listState.status === 'ok' && filtered.length === 0 && (
              <div class="hv-catalog__empty">Kein Produkt trifft die Filter.</div>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                class={
                  'hv-catalog__list-item' +
                  (selectedId === p.id ? ' hv-catalog__list-item--active' : '')
                }
                onClick={() => setSelectedId(p.id)}
              >
                <span class="hv-catalog__list-icon">
                  <ProductIcon osKind={p.osKind} deviceKind={p.deviceKind} size={20} />
                </span>
                <span class="hv-catalog__list-body">
                  <span class="hv-catalog__list-name">{p.displayName}</span>
                  <span class="hv-catalog__list-meta">
                    {p.manufacturerName ?? '—'}
                  </span>
                </span>
                <StatusBadge status={p.approvalStatus} />
              </button>
            ))}
          </div>
        </aside>

        <section class="hv-catalog__detail">
          {selectedId === null ? (
            <div class="hv-catalog__empty">
              Wähle links ein Produkt oder lege ein neues an.
            </div>
          ) : selectedId === '__new__' ? (
            <ProductDetail
              key="__new__"
              productId={null}
              canAdmin={canAdmin}
              canTenantAdmin={canTenantAdmin}
              onSaved={(id) => {
                setSelectedId(id);
                refreshAll();
              }}
              onDeleted={() => {
                setSelectedId(null);
                refreshAll();
              }}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <ProductDetail
              key={selectedId}
              productId={selectedId}
              canAdmin={canAdmin}
              canTenantAdmin={canTenantAdmin}
              onSaved={() => refreshAll()}
              onDeleted={() => {
                setSelectedId(null);
                refreshAll();
              }}
              onClose={() => setSelectedId(null)}
            />
          )}
        </section>
      </div>
    </AppShell>
  );
}

function StatusChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      class={'hv-catalog__chip' + (active ? ' hv-catalog__chip--active' : '')}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function StatusBadge({ status }: { status: ClientProductApprovalStatus }) {
  return (
    <span class={`hv-catalog__status hv-catalog__status--${status}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Detail form
// -----------------------------------------------------------------------------

interface ProductDetailProps {
  productId: string | null;
  canAdmin: boolean;
  canTenantAdmin: boolean;
  onSaved(id: string): void;
  onDeleted(): void;
  onClose(): void;
}

/** Blank form state used both for creation and as a fallback while the
 *  detail fetch is still pending. */
function blankDetail(): ClientProductDetailDto {
  return {
    id: '',
    key: '',
    displayName: '',
    description: null,
    sortOrder: 0,
    enabled: true,
    osKind: 'linux',
    deviceKind: 'gateway',
    iconKey: null,
    approvalStatus: 'draft',
    approvedAt: null,
    approvedByUserId: null,
    approvalNotes: null,
    manufacturerName: null,
    manufacturerAddress: null,
    manufacturerContactEmail: null,
    manufacturerWebsiteUrl: null,
    supplierName: null,
    supplierAddress: null,
    supplierContactEmail: null,
    modelNumber: null,
    hardwareRevision: null,
    productImageUrl: null,
    productImageArtifactId: null,
    productInfoUrl: null,
    ceMarked: false,
    declarationOfConformityUrl: null,
    declarationOfConformityArtifactId: null,
    ceCertificateNumber: null,
    harmonizedStandardsJson: '[]',
    craRiskClass: 'standard',
    vulnerabilityContactEmail: null,
    securityUpdatePolicyUrl: null,
    securitySupportUntil: null,
    sbomFormat: 'none',
    sbomUrl: null,
    sbomArtifactId: null,
    sbomVersion: null,
    sbomUpdatedAt: null,
    minFirmwareVersion: null,
    maxFirmwareVersion: null,
    standardWarrantyMonths: null,
    standardWarrantyText: null,
    ethernetPortsJson: '[]',
    serialPortsJson: '[]',
    createdAt: '',
    updatedAt: '',
  };
}

/** ISO 8601 date-portion for `<input type="date">`. Returns '' on null/invalid
 *  so the input renders empty rather than "Invalid Date". */
function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
/** Turn a `<input type="date">` value into an ISO-8601 timestamp (UTC noon
 *  so timezone rounding doesn't push the date across the boundary). */
function fromDateInput(v: string): string | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const iso = new Date(`${trimmed}T12:00:00Z`).toISOString();
  return iso;
}

function ProductDetail({
  productId,
  canAdmin,
  canTenantAdmin,
  onSaved,
  onDeleted,
  onClose,
}: ProductDetailProps) {
  const isNew = productId === null;
  const [form, setForm] = useState<ClientProductDetailDto>(() => blankDetail());
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load detail on mount for existing products. New products render the blank
  // form immediately.
  useEffect(() => {
    if (isNew) {
      setForm(blankDetail());
      setLoaded(true);
      return;
    }
    const ctrl = new AbortController();
    setLoaded(false);
    setError(null);
    api
      .get<ClientProductDetailDto>(
        `/api/client-catalog/products/${productId}`,
        ctrl.signal,
      )
      .then((d) => {
        setForm(d);
        setLoaded(true);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e.message : 'Konnte Produkt nicht laden.');
        setLoaded(true);
      });
    return () => ctrl.abort();
  }, [productId, isNew]);

  // Tenant-exclusions: loaded once here so the Sperren-section can reflect
  // and toggle the state for the current product. Refetched after each
  // mutation.
  const [exclusionTick, setExclusionTick] = useState(0);
  const exclusionsState = useAsync(
    (signal) =>
      api.get<ListTenantExclusionsResponse>('/api/client-catalog/exclusions', signal),
    [exclusionTick],
  );
  const exclusions: TenantProductExclusionDto[] =
    exclusionsState.status === 'ok' ? exclusionsState.data.items : [];
  const currentExclusion = exclusions.find((e) => e.productId === form.id) ?? null;

  // Helpers to update a single field without re-typing the setState boilerplate.
  const patch = <K extends keyof ClientProductDetailDto>(
    key: K,
    value: ClientProductDetailDto[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!canAdmin) return;
    setSaving(true);
    setError(null);
    try {
      const displayName = form.displayName.trim();
      const key = form.key.trim();
      if (displayName.length === 0) throw new Error('Anzeigename darf nicht leer sein.');
      if (isNew && key.length === 0) throw new Error('Key darf nicht leer sein.');

      // Server accepts JSON as a string; validate before we send so the user
      // sees the parse error inline instead of a 400 with an opaque message.
      const stds = form.harmonizedStandardsJson.trim() || '[]';
      try {
        const parsed = JSON.parse(stds);
        if (!Array.isArray(parsed)) throw new Error('Harmonized Standards muss ein JSON-Array sein.');
      } catch (parseErr) {
        throw new Error(
          `Harmonized Standards JSON ungültig: ${
            parseErr instanceof Error ? parseErr.message : 'Parser-Fehler'
          }`,
        );
      }

      if (isNew) {
        const body: ClientProductCreateRequest = {
          key,
          displayName,
          description: form.description,
          sortOrder: form.sortOrder,
          enabled: form.enabled,
          osKind: form.osKind,
          deviceKind: form.deviceKind,
          iconKey: form.iconKey,
          manufacturerName: form.manufacturerName,
          manufacturerAddress: form.manufacturerAddress,
          manufacturerContactEmail: form.manufacturerContactEmail,
          manufacturerWebsiteUrl: form.manufacturerWebsiteUrl,
          supplierName: form.supplierName,
          supplierAddress: form.supplierAddress,
          supplierContactEmail: form.supplierContactEmail,
          modelNumber: form.modelNumber,
          hardwareRevision: form.hardwareRevision,
          productImageUrl: form.productImageUrl,
          productImageArtifactId: form.productImageArtifactId,
          productInfoUrl: form.productInfoUrl,
          ceMarked: form.ceMarked,
          declarationOfConformityUrl: form.declarationOfConformityUrl,
          declarationOfConformityArtifactId: form.declarationOfConformityArtifactId,
          ceCertificateNumber: form.ceCertificateNumber,
          harmonizedStandardsJson: stds,
          craRiskClass: form.craRiskClass,
          vulnerabilityContactEmail: form.vulnerabilityContactEmail,
          securityUpdatePolicyUrl: form.securityUpdatePolicyUrl,
          securitySupportUntil: form.securitySupportUntil,
          sbomFormat: form.sbomFormat,
          sbomUrl: form.sbomUrl,
          sbomArtifactId: form.sbomArtifactId,
          sbomVersion: form.sbomVersion,
          sbomUpdatedAt: form.sbomUpdatedAt,
          minFirmwareVersion: form.minFirmwareVersion,
          maxFirmwareVersion: form.maxFirmwareVersion,
          standardWarrantyMonths: form.standardWarrantyMonths,
          standardWarrantyText: form.standardWarrantyText,
          ethernetPortsJson: form.ethernetPortsJson,
          serialPortsJson: form.serialPortsJson,
        };
        const res = await api.post<ClientProductDetailDto>(
          '/api/client-catalog/products',
          body,
        );
        showToast({
          tone: 'success',
          title: 'Produkt erstellt',
          message: res.displayName,
          autoHideAfterMs: 2500,
        });
        onSaved(res.id);
      } else {
        // PATCH — send the full form as an update; the server's field-level
        // diff drops no-op columns. Key is not editable post-creation but the
        // server tolerates unchanged values.
        const body: ClientProductUpdateRequest = {
          displayName,
          description: form.description,
          sortOrder: form.sortOrder,
          enabled: form.enabled,
          osKind: form.osKind,
          deviceKind: form.deviceKind,
          iconKey: form.iconKey,
          manufacturerName: form.manufacturerName,
          manufacturerAddress: form.manufacturerAddress,
          manufacturerContactEmail: form.manufacturerContactEmail,
          manufacturerWebsiteUrl: form.manufacturerWebsiteUrl,
          supplierName: form.supplierName,
          supplierAddress: form.supplierAddress,
          supplierContactEmail: form.supplierContactEmail,
          modelNumber: form.modelNumber,
          hardwareRevision: form.hardwareRevision,
          productImageUrl: form.productImageUrl,
          productImageArtifactId: form.productImageArtifactId,
          productInfoUrl: form.productInfoUrl,
          ceMarked: form.ceMarked,
          declarationOfConformityUrl: form.declarationOfConformityUrl,
          declarationOfConformityArtifactId: form.declarationOfConformityArtifactId,
          ceCertificateNumber: form.ceCertificateNumber,
          harmonizedStandardsJson: stds,
          craRiskClass: form.craRiskClass,
          vulnerabilityContactEmail: form.vulnerabilityContactEmail,
          securityUpdatePolicyUrl: form.securityUpdatePolicyUrl,
          securitySupportUntil: form.securitySupportUntil,
          sbomFormat: form.sbomFormat,
          sbomUrl: form.sbomUrl,
          sbomArtifactId: form.sbomArtifactId,
          sbomVersion: form.sbomVersion,
          sbomUpdatedAt: form.sbomUpdatedAt,
          minFirmwareVersion: form.minFirmwareVersion,
          maxFirmwareVersion: form.maxFirmwareVersion,
          standardWarrantyMonths: form.standardWarrantyMonths,
          standardWarrantyText: form.standardWarrantyText,
          ethernetPortsJson: form.ethernetPortsJson,
          serialPortsJson: form.serialPortsJson,
        };
        const res = await api.patch<ClientProductDetailDto>(
          `/api/client-catalog/products/${form.id}`,
          body,
        );
        setForm(res);
        showToast({
          tone: 'success',
          title: 'Produkt gespeichert',
          message: res.displayName,
          autoHideAfterMs: 2500,
        });
        onSaved(res.id);
      }
    } catch (e) {
      const msg = errorMessage(e, 'Speichern fehlgeschlagen.');
      setError(msg);
      showToast({ tone: 'error', title: 'Nicht gespeichert', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!canAdmin || isNew) return;
    const ok = await showConfirm({
      title: 'Produkt hart löschen?',
      message: `Der Katalogeintrag "${form.displayName}" wird endgültig entfernt. Wenn Field-Clients auf das Produkt verweisen, schlägt der Löschvorgang mit 409 fehl.`,
      confirmLabel: 'Endgültig löschen',
      danger: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.delete(`/api/client-catalog/products/${form.id}`);
      showToast({
        tone: 'success',
        title: 'Produkt gelöscht',
        message: form.displayName,
        autoHideAfterMs: 2500,
      });
      onDeleted();
    } catch (e) {
      // 409 with a `referencedByCount` payload is the interesting failure mode.
      if (e instanceof ApiError && e.status === 409) {
        const count =
          (e.body as { referencedByCount?: number } | undefined)?.referencedByCount ?? '?';
        const msg = `Nicht gelöscht: noch von ${count} Client(s) referenziert.`;
        setError(msg);
        showToast({ tone: 'warn', title: 'Referenzen vorhanden', message: msg, autoHideAfterMs: 8000 });
      } else {
        const msg = errorMessage(e, 'Löschen fehlgeschlagen.');
        setError(msg);
        showToast({ tone: 'error', title: 'Nicht gelöscht', message: msg, autoHideAfterMs: 8000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const setApproval = async (target: ClientProductApprovalStatus, notes?: string) => {
    if (!canAdmin || isNew) return;
    setSaving(true);
    setError(null);
    try {
      const body: SetApprovalRequest = { status: target, notes: notes ?? null };
      const res = await api.post<ClientProductDetailDto>(
        `/api/client-catalog/products/${form.id}/approval`,
        body,
      );
      setForm(res);
      showToast({
        tone: 'success',
        title: 'Freigabestatus aktualisiert',
        message: STATUS_LABEL[res.approvalStatus],
        autoHideAfterMs: 2500,
      });
      onSaved(res.id);
    } catch (e) {
      const msg = errorMessage(e, 'Statuswechsel fehlgeschlagen.');
      setError(msg);
      showToast({ tone: 'error', title: 'Nicht übernommen', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const addExclusion = async (reason: string) => {
    if (!canTenantAdmin || isNew) return;
    setSaving(true);
    try {
      const body: TenantProductExclusionRequest = {
        productId: form.id,
        reason: reason.trim() || null,
      };
      await api.post('/api/client-catalog/exclusions', body);
      setExclusionTick((n) => n + 1);
      showToast({
        tone: 'success',
        title: 'Produkt für Tenant gesperrt',
        message: form.displayName,
        autoHideAfterMs: 2500,
      });
    } catch (e) {
      const msg = errorMessage(e, 'Sperre konnte nicht gesetzt werden.');
      setError(msg);
      showToast({ tone: 'error', title: 'Nicht gesperrt', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const removeExclusion = async () => {
    if (!canTenantAdmin || isNew) return;
    setSaving(true);
    try {
      await api.delete(`/api/client-catalog/exclusions/${form.id}`);
      setExclusionTick((n) => n + 1);
      showToast({
        tone: 'success',
        title: 'Sperre aufgehoben',
        message: form.displayName,
        autoHideAfterMs: 2500,
      });
    } catch (e) {
      const msg = errorMessage(e, 'Sperre konnte nicht entfernt werden.');
      setError(msg);
      showToast({ tone: 'error', title: 'Nicht entsperrt', message: msg, autoHideAfterMs: 8000 });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <div class="hv-catalog__empty">Lade…</div>;
  }

  const readOnly = !canAdmin;

  return (
    <>
      <header class="hv-catalog__detail-header">
        <h2 class="hv-catalog__detail-title">
          {isNew ? 'Neues Produkt' : form.displayName || form.key}
        </h2>
        {!isNew && <StatusBadge status={form.approvalStatus} />}
      </header>
      <div class="hv-catalog__detail-body">
        {/* ---------------- Grunddaten ---------------- */}
        <Section title="Grunddaten">
          <div class="hv-plants__form-grid">
            <TextField
              label="Key"
              value={form.key}
              readOnly={!isNew || readOnly}
              onChange={(v) => patch('key', v)}
              placeholder="z.B. unipi-g100"
            />
            <TextField
              label="Anzeigename"
              value={form.displayName}
              readOnly={readOnly}
              onChange={(v) => patch('displayName', v)}
              full
            />
            <TextAreaField
              label="Beschreibung"
              value={form.description ?? ''}
              readOnly={readOnly}
              onChange={(v) => patch('description', v || null)}
              full
              rows={2}
            />
            <SelectField
              label="OS-Kind"
              value={form.osKind}
              readOnly={readOnly}
              options={OS_KIND_OPTIONS}
              onChange={(v) => patch('osKind', v as ClientProductOsKind)}
            />
            <SelectField
              label="Device-Kind"
              value={form.deviceKind}
              readOnly={readOnly}
              options={DEVICE_KIND_OPTIONS}
              onChange={(v) => patch('deviceKind', v as ClientProductDeviceKind)}
            />
            <TextField
              label="Icon-Key (optional)"
              value={form.iconKey ?? ''}
              readOnly={readOnly}
              onChange={(v) => patch('iconKey', v || null)}
            />
            <NumberField
              label="Sort-Order"
              value={form.sortOrder}
              readOnly={readOnly}
              onChange={(n) => patch('sortOrder', n ?? 0)}
            />
            <CheckboxField
              label="Aktiviert"
              checked={form.enabled}
              readOnly={readOnly}
              onChange={(v) => patch('enabled', v)}
            />
          </div>
        </Section>

        {/* ---------------- Hersteller / Lieferant ---------------- */}
        <Section title="Hersteller / Lieferant">
          <div class="hv-plants__form-grid">
            <TextField label="Hersteller" value={form.manufacturerName ?? ''} readOnly={readOnly} onChange={(v) => patch('manufacturerName', v || null)} />
            <TextField label="Hersteller-Adresse" value={form.manufacturerAddress ?? ''} readOnly={readOnly} onChange={(v) => patch('manufacturerAddress', v || null)} />
            <TextField label="Hersteller E-Mail" value={form.manufacturerContactEmail ?? ''} readOnly={readOnly} onChange={(v) => patch('manufacturerContactEmail', v || null)} />
            <TextField label="Hersteller-Website" value={form.manufacturerWebsiteUrl ?? ''} readOnly={readOnly} onChange={(v) => patch('manufacturerWebsiteUrl', v || null)} />
            <TextField label="Lieferant" value={form.supplierName ?? ''} readOnly={readOnly} onChange={(v) => patch('supplierName', v || null)} />
            <TextField label="Lieferant-Adresse" value={form.supplierAddress ?? ''} readOnly={readOnly} onChange={(v) => patch('supplierAddress', v || null)} />
            <TextField label="Lieferant E-Mail" value={form.supplierContactEmail ?? ''} readOnly={readOnly} onChange={(v) => patch('supplierContactEmail', v || null)} />
            <TextField label="Modell-Nr." value={form.modelNumber ?? ''} readOnly={readOnly} onChange={(v) => patch('modelNumber', v || null)} />
            <TextField label="Hardware-Revision" value={form.hardwareRevision ?? ''} readOnly={readOnly} onChange={(v) => patch('hardwareRevision', v || null)} />
          </div>
        </Section>

        {/* ---------------- Produkt-Assets ---------------- */}
        <Section title="Produkt-Assets">
          <div class="hv-plants__form-grid">
            <TextField label="Produkt-Bild URL" value={form.productImageUrl ?? ''} readOnly={readOnly} onChange={(v) => patch('productImageUrl', v || null)} full />
            <TextField label="Produkt-Bild Artifact-ID" value={form.productImageArtifactId ?? ''} readOnly={readOnly} onChange={(v) => patch('productImageArtifactId', v || null)} placeholder="Guid" />
            <TextField label="Produkt-Info URL" value={form.productInfoUrl ?? ''} readOnly={readOnly} onChange={(v) => patch('productInfoUrl', v || null)} />
          </div>
        </Section>

        {/* ---------------- CE / Konformität ---------------- */}
        <Section title="CE / Konformität">
          <div class="hv-plants__form-grid">
            <CheckboxField label="CE-markiert" checked={form.ceMarked} readOnly={readOnly} onChange={(v) => patch('ceMarked', v)} />
            <TextField label="CE-Zertifikat-Nr." value={form.ceCertificateNumber ?? ''} readOnly={readOnly} onChange={(v) => patch('ceCertificateNumber', v || null)} />
            <TextField label="Konformitätserklärung URL" value={form.declarationOfConformityUrl ?? ''} readOnly={readOnly} onChange={(v) => patch('declarationOfConformityUrl', v || null)} full />
            <TextField label="Konformitätserklärung Artifact-ID" value={form.declarationOfConformityArtifactId ?? ''} readOnly={readOnly} onChange={(v) => patch('declarationOfConformityArtifactId', v || null)} placeholder="Guid" />
            <TextAreaField
              label="Harmonisierte Normen (JSON-Array)"
              value={form.harmonizedStandardsJson}
              readOnly={readOnly}
              onChange={(v) => patch('harmonizedStandardsJson', v)}
              full
              rows={3}
              placeholder='["EN 60335-1:2020","EN 55014-1:2021"]'
            />
          </div>
        </Section>

        {/* ---------------- CRA-Cyber ---------------- */}
        <Section title="CRA-Cyber">
          <div class="hv-plants__form-grid">
            <SelectField
              label="CRA-Risikoklasse"
              value={form.craRiskClass}
              readOnly={readOnly}
              options={CRA_OPTIONS}
              onChange={(v) => patch('craRiskClass', v as CraRiskClass)}
            />
            <TextField label="Vulnerability-Kontakt E-Mail" value={form.vulnerabilityContactEmail ?? ''} readOnly={readOnly} onChange={(v) => patch('vulnerabilityContactEmail', v || null)} />
            <TextField label="Security-Update-Policy URL" value={form.securityUpdatePolicyUrl ?? ''} readOnly={readOnly} onChange={(v) => patch('securityUpdatePolicyUrl', v || null)} full />
            <DateField
              label="Security-Support bis"
              value={toDateInput(form.securitySupportUntil)}
              readOnly={readOnly}
              onChange={(v) => patch('securitySupportUntil', fromDateInput(v))}
            />
          </div>
        </Section>

        {/* ---------------- SBOM ---------------- */}
        <Section title="SBOM">
          <div class="hv-plants__form-grid">
            <SelectField
              label="SBOM-Format"
              value={form.sbomFormat}
              readOnly={readOnly}
              options={SBOM_OPTIONS}
              onChange={(v) => patch('sbomFormat', v as SbomFormatWire)}
            />
            <TextField label="SBOM URL" value={form.sbomUrl ?? ''} readOnly={readOnly} onChange={(v) => patch('sbomUrl', v || null)} />
            <TextField label="SBOM Artifact-ID" value={form.sbomArtifactId ?? ''} readOnly={readOnly} onChange={(v) => patch('sbomArtifactId', v || null)} placeholder="Guid" />
            <TextField label="SBOM Version" value={form.sbomVersion ?? ''} readOnly={readOnly} onChange={(v) => patch('sbomVersion', v || null)} />
            <DateField
              label="SBOM aktualisiert am"
              value={toDateInput(form.sbomUpdatedAt)}
              readOnly={readOnly}
              onChange={(v) => patch('sbomUpdatedAt', fromDateInput(v))}
            />
          </div>
        </Section>

        {/* ---------------- Firmware ---------------- */}
        <Section title="Firmware">
          <div class="hv-plants__form-grid">
            <TextField label="Min-Firmware" value={form.minFirmwareVersion ?? ''} readOnly={readOnly} onChange={(v) => patch('minFirmwareVersion', v || null)} />
            <TextField label="Max-Firmware" value={form.maxFirmwareVersion ?? ''} readOnly={readOnly} onChange={(v) => patch('maxFirmwareVersion', v || null)} />
          </div>
        </Section>

        {/* ---------------- Garantie ---------------- */}
        <Section title="Garantie (Standard)">
          <div class="hv-plants__form-grid">
            <NumberField
              label="Standard-Garantie (Monate)"
              value={form.standardWarrantyMonths}
              readOnly={readOnly}
              onChange={(n) => patch('standardWarrantyMonths', n)}
            />
            <TextAreaField
              label="Garantietext"
              value={form.standardWarrantyText ?? ''}
              readOnly={readOnly}
              onChange={(v) => patch('standardWarrantyText', v || null)}
              full
              rows={3}
            />
          </div>
        </Section>

        {/* ---------------- Port-Katalog (Modell-Ebene) ---------------- */}
        <Section title="Ports (Modell-Ebene)">
          <p class="muted" style={{ fontSize: '0.85rem', margin: '0 0 8px 0' }}>
            Beschriftung auf dem Blech: <code>Serial A</code> / <code>Serial B</code> für Serial-Ports,
            <code>eth0</code>/<code>eth1</code> für Ethernet. Nur Ports die am RS485-/RS232-Transceiver
            hängen sollten hier stehen — andere UARTs wären Klick-Fallen im Dropdown.
          </p>
          <div class="hv-plants__form-grid">
            <TextAreaField
              label="Ethernet-Ports (JSON-Array)"
              value={form.ethernetPortsJson}
              readOnly={readOnly}
              onChange={(v) => patch('ethernetPortsJson', v || '[]')}
              full
              rows={3}
            />
            <TextAreaField
              label='Serial-Ports (JSON-Array: [{"name":"Serial A","physical":"/dev/ttyS0","kind":"rs485"}])'
              value={form.serialPortsJson}
              readOnly={readOnly}
              onChange={(v) => patch('serialPortsJson', v || '[]')}
              full
              rows={5}
            />
          </div>
        </Section>

        {/* ---------------- Freigabe ---------------- */}
        {!isNew && (
          <Section title="Freigabe">
            <div class="hv-catalog__approval-actions">
              <StatusBadge status={form.approvalStatus} />
              {canAdmin && form.approvalStatus === 'draft' && (
                <button type="button" class="hv-btn hv-btn--sm" disabled={saving}
                        onClick={() => setApproval('pending_review')}>
                  Zur Prüfung
                </button>
              )}
              {canAdmin && form.approvalStatus === 'pending_review' && (
                <>
                  <button type="button" class="hv-btn hv-btn--sm hv-btn--primary" disabled={saving}
                          onClick={() => setApproval('approved')}>
                    Freigeben
                  </button>
                  <button type="button" class="hv-btn hv-btn--sm hv-btn--danger-outline" disabled={saving}
                          onClick={() => setApproval('rejected')}>
                    Ablehnen
                  </button>
                  <button type="button" class="hv-btn hv-btn--sm" disabled={saving}
                          onClick={() => setApproval('draft')}>
                    Zurück zu Draft
                  </button>
                </>
              )}
              {canAdmin && form.approvalStatus === 'approved' && (
                <button type="button" class="hv-btn hv-btn--sm hv-btn--danger-outline" disabled={saving}
                        onClick={() => setApproval('retired')}>
                  Zurückziehen (Retire)
                </button>
              )}
              {canAdmin && form.approvalStatus === 'retired' && (
                <button type="button" class="hv-btn hv-btn--sm" disabled={saving}
                        onClick={() => setApproval('approved')}>
                  Reaktivieren
                </button>
              )}
              {canAdmin && form.approvalStatus === 'rejected' && (
                <button type="button" class="hv-btn hv-btn--sm" disabled={saving}
                        onClick={() => setApproval('draft')}>
                  Zurück zu Draft
                </button>
              )}
            </div>
            {(form.approvedAt || form.approvedByUserId || form.approvalNotes) && (
              <div class="hv-catalog__approval-meta">
                {form.approvedAt && <span>Freigegeben: {new Date(form.approvedAt).toLocaleString()}</span>}
                {form.approvedByUserId && <span>Durch User: {form.approvedByUserId}</span>}
                {form.approvalNotes && <span>Notizen: {form.approvalNotes}</span>}
              </div>
            )}
          </Section>
        )}

        {/* ---------------- Sperren (Tenant) ---------------- */}
        {!isNew && canTenantAdmin && (
          <TenantExclusionSection
            productDisplayName={form.displayName}
            currentExclusion={currentExclusion}
            saving={saving}
            onAdd={addExclusion}
            onRemove={removeExclusion}
          />
        )}

        {error && <p class="hv-modal__error">{error}</p>}
      </div>

      <div class="hv-catalog__detail-footer">
        {canAdmin && !isNew && (
          <button type="button" class="hv-btn hv-btn--danger-outline" onClick={del} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconDeleteHard size={14} />Löschen
            </span>
          </button>
        )}
        <div class="hv-catalog__detail-footer-spacer" />
        <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconCancel size={14} />Schließen
          </span>
        </button>
        {canAdmin && (
          <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <IconSave size={14} />Speichern
            </span>
          </button>
        )}
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <div class="hv-plants__form-section">
      <h3 class="hv-plants__form-section-title">{title}</h3>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Field primitives — thin wrappers around .hv-modal__field so every section
// stays visually consistent without repeating the class strings inline.
// -----------------------------------------------------------------------------

function TextField({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
  full,
}: {
  label: string;
  value: string;
  onChange(v: string): void;
  readOnly?: boolean;
  placeholder?: string;
  full?: boolean;
}) {
  return (
    <div class={'hv-modal__field' + (full ? ' hv-plants__form-grid--full' : '')}>
      <label>{label}</label>
      <input
        type="text"
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        placeholder={placeholder}
        onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
      />
    </div>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
  full,
  rows = 2,
}: {
  label: string;
  value: string;
  onChange(v: string): void;
  readOnly?: boolean;
  placeholder?: string;
  full?: boolean;
  rows?: number;
}) {
  return (
    <div class={'hv-modal__field' + (full ? ' hv-plants__form-grid--full' : '')}>
      <label>{label}</label>
      <textarea
        rows={rows}
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        placeholder={placeholder}
        onInput={(e) => onChange((e.currentTarget as HTMLTextAreaElement).value)}
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: number | null;
  onChange(v: number | null): void;
  readOnly?: boolean;
}) {
  return (
    <div class="hv-modal__field">
      <label>{label}</label>
      <input
        type="number"
        value={value === null ? '' : String(value)}
        readOnly={readOnly}
        disabled={readOnly}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value.trim();
          if (raw === '') {
            onChange(null);
            return;
          }
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : null);
        }}
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange(v: string): void;
  readOnly?: boolean;
}) {
  return (
    <div class="hv-modal__field">
      <label>{label}</label>
      <select
        value={value}
        disabled={readOnly}
        onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  onChange(v: string): void;
  readOnly?: boolean;
}) {
  return (
    <div class="hv-modal__field">
      <label>{label}</label>
      <input
        type="date"
        value={value}
        readOnly={readOnly}
        disabled={readOnly}
        onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
      />
    </div>
  );
}

function CheckboxField({
  label,
  checked,
  onChange,
  readOnly,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
  readOnly?: boolean;
}) {
  return (
    <div class="hv-modal__field">
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={readOnly}
          onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        {label}
      </label>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tenant-exclusion sub-section — its own component because it carries local
// state (the reason textarea) that the parent form doesn't need to know about.
// -----------------------------------------------------------------------------

function TenantExclusionSection({
  productDisplayName,
  currentExclusion,
  saving,
  onAdd,
  onRemove,
}: {
  productDisplayName: string;
  currentExclusion: TenantProductExclusionDto | null;
  saving: boolean;
  onAdd(reason: string): void;
  onRemove(): void;
}) {
  const isExcluded = currentExclusion !== null;
  const [reason, setReason] = useState<string>(currentExclusion?.reason ?? '');
  // When the caller flips between products, seed the reason field with the
  // currently persisted reason (if any). No effect: the section unmounts +
  // remounts per productId key anyway.

  return (
    <Section title="Sperren (Tenant)">
      <div class="hv-modal__field">
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={isExcluded}
            disabled={saving}
            onChange={(e) => {
              const on = (e.currentTarget as HTMLInputElement).checked;
              if (on) onAdd(reason);
              else onRemove();
            }}
          />
          Für unseren Tenant sperren
        </label>
      </div>
      <div class="hv-modal__field">
        <label>Grund (optional)</label>
        <textarea
          rows={2}
          value={reason}
          disabled={saving}
          onInput={(e) => setReason((e.currentTarget as HTMLTextAreaElement).value)}
          placeholder={`z.B. "in ${productDisplayName} passt nicht ins Portfolio"`}
        />
      </div>
      {isExcluded && currentExclusion && (
        <p class="hv-modal__hint">
          Gesperrt seit {new Date(currentExclusion.excludedAt).toLocaleString()}
          {currentExclusion.reason && ` — ${currentExclusion.reason}`}
        </p>
      )}
    </Section>
  );
}

// -----------------------------------------------------------------------------
// Error extractor — same pattern as PlantEditModal so operators see a real
// server message instead of the generic "Fehler" tokens.
// -----------------------------------------------------------------------------

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as
      | { error?: string; detail?: string; title?: string }
      | undefined;
    const serverMsg = body?.error ?? body?.detail ?? body?.title;
    return `HTTP ${err.status}${serverMsg ? ' — ' + serverMsg : ''}`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
